/* eslint-disable max-lines-per-function */
import { ChangeEventHandler, useEffect, useMemo, useState } from 'react';

import { Checkbox, CheckboxProps, ChevronDownIcon } from '../..';
import { updateBoxState } from '../radio-accordion/checkbox-common';

type AccordionCheckboxProps = Omit<CheckboxProps, 'hideLabel'>;
export interface CheckboxAccordionProps extends Omit<CheckboxProps, 'isChecked'> {
  checkboxData: AccordionCheckboxProps[];
  defaultOpen: boolean;
  onCheckboxChange: (checkboxValue: string, selectedBoxes: string[]) => void;
}

export const CheckboxAccordion = (props: CheckboxAccordionProps) => {
  const { checkboxData, defaultOpen, ...checkboxProps } = props;
  const [open, setOpen] = useState(defaultOpen);
  const [boxes, setBoxes] = useState(checkboxData);

  const noneChecked = useMemo(() => boxes.every((box) => !box.isChecked), [boxes]);
  const allChecked = useMemo(() => boxes.every((box) => box.isChecked), [boxes]);
  const isPartiallyChecked = !noneChecked && !allChecked;

  useEffect(() => {
    setBoxes(checkboxData);
  }, [checkboxData]);

  const onMainCheckboxChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    const checked = e.target.checked;
    setBoxes(updateBoxState(checked));
    checkboxProps.onCheckboxChange(
      checkboxProps.value,
      checked
        ? boxes.map((box) => {
            return box.value;
          })
        : []
    );
  };

  const onCheckboxChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    e.stopPropagation();
    setBoxes((curr) => {
      return curr.map((box) => {
        if (e.target.value !== box.value) return box;
        return { ...box, isChecked: e.target.checked };
      });
    });

    checkboxProps.onCheckboxChange(
      checkboxProps.value,
      boxes
        .filter((box) => {
          return box.isChecked ? e.target.value !== box.value : e.target.value === box.value;
        })
        .map((box) => box.value)
    );
  };

  useEffect(() => {
    setOpen(!noneChecked);
  }, [noneChecked]);

  return (
    <div className="flex flex-col">
      <div className="flex cursor-pointer flex-row">
        <div className="flex-1">
          <Checkbox
            {...checkboxProps}
            isChecked={allChecked}
            isPartiallyChecked={isPartiallyChecked}
            onChange={onMainCheckboxChange}
          />
        </div>
        <ChevronDownIcon
          onClick={() => {
            setOpen((curr) => !curr);
          }}
          className={`max-h-6 max-w-6 transition-all ${open ? 'rotate-180	' : 'rotate-0'}`}
        />
      </div>
      {open && (
        <div className="ml-10 mt-4 space-y-4">
          {boxes.map((props, index) => {
            return <Checkbox key={index} {...props} hideLabel={false} onChange={onCheckboxChange} />;
          })}
        </div>
      )}
    </div>
  );
};

CheckboxAccordion.displayName = 'CheckboxAccordion';

export default CheckboxAccordion;
